import confetti from 'canvas-confetti';
import { useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { PartyPopper, Sparkles, Rocket } from 'lucide-react';

interface CelebrationModalProps {
  prenom: string;
  onClose: () => void;
}

export function CelebrationModal({ prenom, onClose }: CelebrationModalProps) {
  useEffect(() => {
    // Lancer les confettis au montage
    const duration = 3000;
    const end = Date.now() + duration;

    const frame = () => {
      // Confettis depuis la gauche
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6']
      });
      // Confettis depuis la droite
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6']
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };
    frame();

    // Grand burst initial
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#3b82f6', '#10b981', '#f59e0b']
    });
  }, []);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md text-center border-primary/20">
        <DialogHeader className="sr-only">
          <DialogTitle>Félicitations</DialogTitle>
        </DialogHeader>

        <div className="flex justify-center mb-4">
          <div className="relative">
            <PartyPopper className="h-16 w-16 text-yellow-500 animate-bounce" />
            <div className="absolute -top-1 -right-1 h-4 w-4 bg-primary rounded-full animate-ping" />
          </div>
        </div>

        <h2 className="text-2xl font-bold mb-2">
          Félicitations {prenom} !
        </h2>

        <p className="text-muted-foreground mb-4">
          Tu as terminé les <strong className="text-foreground">Premiers pas</strong> !
        </p>

        <div className="bg-muted/50 rounded-lg p-4 mb-4 text-left border border-border/50">
          <p className="font-medium mb-3 text-sm">Tu peux maintenant :</p>
          <ul className="space-y-2.5 text-sm">
            <li className="flex items-start gap-2">
              <Sparkles className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
              <span>Piloter la rentabilité de tes chantiers en temps réel</span>
            </li>
            <li className="flex items-start gap-2">
              <Sparkles className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
              <span>Gérer tes effectifs de façon intuitive</span>
            </li>
            <li className="flex items-start gap-2">
              <Sparkles className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
              <span>Anticiper les retards et leurs impacts financiers</span>
            </li>
          </ul>
        </div>

        <p className="text-sm text-muted-foreground mb-6">
          💡 Le <strong className="text-foreground">Guide</strong> reste accessible depuis le tableau de bord
          pour t'accompagner sur toutes les fonctionnalités.
        </p>

        <Button onClick={onClose} className="w-full" size="lg">
          <Rocket className="mr-2 h-4 w-4" />
          Commencer à piloter
        </Button>
      </DialogContent>
    </Dialog>
  );
}
